/**
 * System MCP Template Edit Page.
 *
 * Admin-only page for editing existing MCP templates.
 * Loads current template data and allows editing all fields
 * including JSON configuration schemas.
 *
 * Sprint: 41, Task: S41-A03
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { formResolver } from '@/lib/form-resolver';
import { useI18n } from '@/i18n';
import {
  Button,
  Card,
  Input,
  Label,
  Spinner,
  Textarea,
  IconPicker,
  CustomIconUpload,
} from '@/components/ui';
import { LlmPromptAugmentationCard } from './components/LlmPromptAugmentationCard';
import {
  useSystemTemplate,
  useUpdateSystemTemplate,
  useUploadTemplateIcon,
  useDeleteTemplateIcon,
} from '@/features/mcp';
import {
  mcpTemplateFormSchema,
  type MCPTemplateForm,
} from '@/features/mcp/schemas';
import { BasePoliciesForm } from '@/features/mcp/components/base-policies-form';
import { DefaultToolsEditor } from '@/features/mcp/components/default-tools-editor';
import { TemplateDocumentation } from '@/features/mcp/components/template-documentation';
import {
  ArrowLeft,
  Save,
  Settings2,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';

// =============================================================================
// Constants
// =============================================================================

const CATEGORY_OPTIONS = [
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'itsm', label: 'IT Service Management' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'communication', label: 'Communication' },
  { value: 'custom', label: 'Custom' },
];

const TOOL_DISCOVERY_OPTIONS = [
  { value: 'static', label: 'Static' },
  { value: 'dynamic', label: 'Dynamic' },
];

const EXECUTION_TIER_OPTIONS = [
  { value: 'platform_http', labelKey: 'admin.systemTemplates.form.executionTierPlatformHttp' },
  { value: 'agent_managed', labelKey: 'admin.systemTemplates.form.executionTierAgentManaged' },
  { value: 'platform_native', labelKey: 'admin.systemTemplates.form.executionTierPlatformNative' },
];

const SETUP_COMPLEXITY_OPTIONS = [
  { value: 'simple', labelKey: 'admin.systemTemplates.form.setupComplexitySimple' },
  { value: 'intermediate', labelKey: 'admin.systemTemplates.form.setupComplexityIntermediate' },
  { value: 'advanced', labelKey: 'admin.systemTemplates.form.setupComplexityAdvanced' },
];

// =============================================================================
// JSON Editor Component
// =============================================================================

function JsonEditor({
  label,
  help,
  value,
  onChange,
  error,
  rows = 8,
}: {
  label: string;
  help: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  rows?: number;
}) {
  const { t } = useI18n();
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <p className="text-xs text-muted-foreground">{help}</p>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="font-mono text-xs"
        placeholder="{}"
      />
      {error && (
        <p className="text-xs text-destructive">{t('admin.systemTemplates.form.invalidJson')}</p>
      )}
    </div>
  );
}

// =============================================================================
// Page Component
// =============================================================================

export function SystemTemplateEditPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'form' | 'docs'>('form');
  const { data: template, isLoading, error: loadError } = useSystemTemplate(slug!);
  const updateMutation = useUpdateSystemTemplate();
  const uploadIconMutation = useUploadTemplateIcon();
  const deleteIconMutation = useDeleteTemplateIcon();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<MCPTemplateForm>({
    resolver: formResolver(mcpTemplateFormSchema),
    defaultValues: {
      name: '',
      slug: '',
      display_name: '',
      description: '',
      icon: '',
      category: 'monitoring',
      config_schema: '{}',
      secrets_schema: '{}',
      base_policies: '{}',
      default_tools: '[]',
      tool_discovery_mode: 'static',
      documentation_url: '',
      setup_instructions: '',
      is_beta: false,
      execution_tier: 'platform_http',
      template_version: '1.0.0',
      tags: [],
      setup_complexity: 'simple',
    },
  });

  // Populate form when template loads
  useEffect(() => {
    if (template) {
      reset({
        name: template.name,
        slug: template.slug,
        display_name: template.display_name,
        description: template.description ?? '',
        icon: template.icon ?? '',
        category: template.category as MCPTemplateForm['category'],
        config_schema: JSON.stringify(template.config_schema ?? {}, null, 2),
        secrets_schema: JSON.stringify(template.secrets_schema ?? {}, null, 2),
        base_policies: JSON.stringify(template.base_policies ?? {}, null, 2),
        default_tools: JSON.stringify(template.default_tools ?? [], null, 2),
        tool_discovery_mode: (template.tool_discovery_mode as 'static' | 'dynamic') || 'static',
        documentation_url: template.documentation_url ?? '',
        setup_instructions: template.setup_instructions ?? '',
        is_beta: template.is_beta,
        execution_tier: (template.execution_tier as 'platform_http' | 'agent_managed' | 'platform_native') || 'platform_http',
        template_version: template.template_version || '1.0.0',
        tags: template.tags ?? [],
        setup_complexity: (template.setup_complexity as 'simple' | 'intermediate' | 'advanced') || 'simple',
      });
    }
  }, [template, reset]);

  const onSubmit = async (formData: MCPTemplateForm) => {
    if (!slug) return;
    try {
      const payload: Record<string, unknown> = {
        name: formData.name,
        display_name: formData.display_name,
        description: formData.description || undefined,
        icon: formData.icon || undefined,
        category: formData.category,
        config_schema: JSON.parse(formData.config_schema || '{}'),
        secrets_schema: JSON.parse(formData.secrets_schema || '{}'),
        base_policies: JSON.parse(formData.base_policies || '{}'),
        default_tools: JSON.parse(formData.default_tools || '[]'),
        tool_discovery_mode: formData.tool_discovery_mode,
        documentation_url: formData.documentation_url || undefined,
        setup_instructions: formData.setup_instructions || undefined,
        is_beta: formData.is_beta,
        execution_tier: formData.execution_tier,
        template_version: formData.template_version,
        tags: formData.tags,
        setup_complexity: formData.setup_complexity,
      };

      await updateMutation.mutateAsync({ slug, data: payload });
      toast.success(t('admin.systemTemplates.updateSuccess'));
      navigate('/integrations/templates');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update template');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  if (loadError || !template) {
    return (
      <Card className="p-8 text-center">
        <p className="text-destructive">Template not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/integrations/templates')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/integrations/templates')}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings2 className="w-6 h-6 text-primary" />
            {t('admin.systemTemplates.editTemplate')}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{template.slug}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        <button
          type="button"
          onClick={() => setActiveTab('form')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'form'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Settings2 className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          {t('admin.systemTemplates.editTemplate')}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('docs')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'docs'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <FileText className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          {t('admin.systemTemplates.form.documentationTab')}
        </button>
      </div>

      {/* Documentation Tab */}
      {activeTab === 'docs' && (
        <Card className="p-6">
          <TemplateDocumentation slug={slug!} />
        </Card>
      )}

      {/* Form Tab */}
      {activeTab === 'form' && (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Identity Section */}
        <Card className="p-6 space-y-4">
          <h2 className="font-semibold text-sm border-b pb-2">
            {t('admin.systemTemplates.form.identity')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <div className="space-y-1.5">
                  <Label htmlFor="name">{t('admin.systemTemplates.form.name')} *</Label>
                  <p className="text-xs text-muted-foreground">{t('admin.systemTemplates.form.nameHelp')}</p>
                  <Input id="name" {...field} />
                  {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
                </div>
              )}
            />

            {/* Slug (read-only) */}
            <Controller
              name="slug"
              control={control}
              render={({ field }) => (
                <div className="space-y-1.5">
                  <Label htmlFor="slug">{t('admin.systemTemplates.form.slug')}</Label>
                  <p className="text-xs text-muted-foreground">{t('admin.systemTemplates.form.slugHelp')}</p>
                  <Input id="slug" {...field} disabled className="bg-muted" />
                </div>
              )}
            />

            {/* Display Name */}
            <Controller
              name="display_name"
              control={control}
              render={({ field }) => (
                <div className="space-y-1.5">
                  <Label htmlFor="display_name">{t('admin.systemTemplates.form.displayName')} *</Label>
                  <p className="text-xs text-muted-foreground">{t('admin.systemTemplates.form.displayNameHelp')}</p>
                  <Input id="display_name" {...field} />
                  {errors.display_name && <p className="text-xs text-destructive">{errors.display_name.message}</p>}
                </div>
              )}
            />

            {/* Category */}
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <div className="space-y-1.5">
                  <Label htmlFor="category">{t('admin.systemTemplates.form.category')} *</Label>
                  <select
                    id="category"
                    {...field}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}
            />

            {/* Icon (Lucide fallback) */}
            <Controller
              name="icon"
              control={control}
              render={({ field }) => (
                <div className="space-y-1.5">
                  <Label htmlFor="icon">{t('admin.systemTemplates.form.icon')}</Label>
                  <p className="text-xs text-muted-foreground">{t('admin.systemTemplates.form.iconHelp')}</p>
                  <IconPicker
                    value={field.value}
                    onChange={field.onChange}
                  />
                </div>
              )}
            />

            {/* Custom vendor icon (overrides Lucide) */}
            <CustomIconUpload
              slug={slug}
              hasCustomIcon={template?.has_custom_icon ?? false}
              onUpload={(file) => {
                if (slug) {
                  uploadIconMutation.mutate({ slug, file });
                }
              }}
              onRemove={() => {
                if (slug) {
                  deleteIconMutation.mutate(slug);
                }
              }}
              isPending={uploadIconMutation.isPending || deleteIconMutation.isPending}
            />

            {/* Beta */}
            <Controller
              name="is_beta"
              control={control}
              render={({ field }) => (
                <div className="space-y-1.5 flex items-center gap-3 pt-6">
                  <input
                    type="checkbox"
                    id="is_beta"
                    checked={field.value}
                    onChange={field.onChange}
                    className="rounded border-gray-300"
                  />
                  <div>
                    <Label htmlFor="is_beta">{t('admin.systemTemplates.form.isBeta')}</Label>
                    <p className="text-xs text-muted-foreground">{t('admin.systemTemplates.form.isBetaHelp')}</p>
                  </div>
                </div>
              )}
            />
          </div>

          {/* Description */}
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <div className="space-y-1.5">
                <Label htmlFor="description">{t('admin.systemTemplates.form.description')}</Label>
                <Textarea
                  id="description"
                  {...field}
                  rows={3}
                  placeholder={t('admin.systemTemplates.form.descriptionPlaceholder')}
                />
                {errors.description && <p className="text-xs text-destructive">{errors.description.message}</p>}
              </div>
            )}
          />
        </Card>

        {/* Configuration Section */}
        <Card className="p-6 space-y-4">
          <h2 className="font-semibold text-sm border-b pb-2">
            {t('admin.systemTemplates.form.configuration')}
          </h2>

          <Controller
            name="config_schema"
            control={control}
            render={({ field }) => (
              <JsonEditor
                label={t('admin.systemTemplates.form.configSchema')}
                help={t('admin.systemTemplates.form.configSchemaHelp')}
                value={field.value}
                onChange={field.onChange}
                error={errors.config_schema?.message}
                rows={12}
              />
            )}
          />

          <Controller
            name="secrets_schema"
            control={control}
            render={({ field }) => (
              <JsonEditor
                label={t('admin.systemTemplates.form.secretsSchema')}
                help={t('admin.systemTemplates.form.secretsSchemaHelp')}
                value={field.value}
                onChange={field.onChange}
                error={errors.secrets_schema?.message}
                rows={8}
              />
            )}
          />
        </Card>

        {/* Tools Section */}
        <Card className="p-6 space-y-4">
          <h2 className="font-semibold text-sm border-b pb-2">
            {t('admin.systemTemplates.form.tools')}
          </h2>

          <Controller
            name="default_tools"
            control={control}
            render={({ field }) => (
              <DefaultToolsEditor
                value={field.value}
                onChange={field.onChange}
                error={errors.default_tools?.message}
              />
            )}
          />

          <Controller
            name="tool_discovery_mode"
            control={control}
            render={({ field }) => (
              <div className="space-y-1.5">
                <Label htmlFor="tool_discovery_mode">
                  {t('admin.systemTemplates.form.toolDiscoveryMode')}
                </Label>
                <select
                  id="tool_discovery_mode"
                  {...field}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  {TOOL_DISCOVERY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}
          />
        </Card>

        {/* Security Section */}
        <Card className="p-6 space-y-4">
          <h2 className="font-semibold text-sm border-b pb-2">
            {t('admin.systemTemplates.form.security')}
          </h2>

          <Controller
            name="base_policies"
            control={control}
            render={({ field }) => (
              <BasePoliciesForm
                value={field.value}
                onChange={field.onChange}
                error={errors.base_policies?.message}
              />
            )}
          />
        </Card>

        {/* LLM Prompt Augmentation (read-only from template.yaml) */}
        <LlmPromptAugmentationCard llmPrompts={template.llm_prompts} />

        {/* Metadata Section */}
        <Card className="p-6 space-y-4">
          <h2 className="font-semibold text-sm border-b pb-2">
            {t('admin.systemTemplates.form.metadata')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Execution Tier */}
            <Controller
              name="execution_tier"
              control={control}
              render={({ field }) => (
                <div className="space-y-1.5">
                  <Label>{t('admin.systemTemplates.form.executionTier')}</Label>
                  <p className="text-xs text-muted-foreground">{t('admin.systemTemplates.form.executionTierHelp')}</p>
                  <div className="flex gap-4 pt-1">
                    {EXECUTION_TIER_OPTIONS.map((opt) => (
                      <label key={opt.value} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name="execution_tier"
                          value={opt.value}
                          checked={field.value === opt.value}
                          onChange={() => field.onChange(opt.value)}
                          className="text-primary"
                        />
                        {t(opt.labelKey)}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            />

            {/* Template Version */}
            <Controller
              name="template_version"
              control={control}
              render={({ field }) => (
                <div className="space-y-1.5">
                  <Label>{t('admin.systemTemplates.form.templateVersion')}</Label>
                  <p className="text-xs text-muted-foreground">{t('admin.systemTemplates.form.templateVersionHelp')}</p>
                  <Input
                    {...field}
                    placeholder="1.0.0"
                  />
                  {errors.template_version && <p className="text-xs text-destructive">{errors.template_version.message}</p>}
                </div>
              )}
            />

            {/* Setup Complexity */}
            <Controller
              name="setup_complexity"
              control={control}
              render={({ field }) => (
                <div className="space-y-1.5">
                  <Label>{t('admin.systemTemplates.form.setupComplexity')}</Label>
                  <p className="text-xs text-muted-foreground">{t('admin.systemTemplates.form.setupComplexityHelp')}</p>
                  <select
                    {...field}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    {SETUP_COMPLEXITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                    ))}
                  </select>
                </div>
              )}
            />
          </div>

          {/* Tags */}
          <Controller
            name="tags"
            control={control}
            render={({ field }) => {
              const tags = field.value ?? [];
              const addTag = (tag: string) => {
                const trimmed = tag.trim();
                if (trimmed && !tags.includes(trimmed)) {
                  field.onChange([...tags, trimmed]);
                }
              };
              const removeTag = (index: number) => {
                field.onChange(tags.filter((_: string, i: number) => i !== index));
              };
              return (
                <div className="space-y-1.5">
                  <Label>{t('admin.systemTemplates.form.tags')}</Label>
                  <p className="text-xs text-muted-foreground">{t('admin.systemTemplates.form.tagsHelp')}</p>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {tags.map((tag: string, i: number) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground text-xs"
                      >
                        {tag}
                        <button type="button" onClick={() => removeTag(i)} className="text-muted-foreground hover:text-destructive ml-0.5">×</button>
                      </span>
                    ))}
                  </div>
                  <Input
                    placeholder={t('admin.systemTemplates.form.tagsPlaceholder')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTag((e.target as HTMLInputElement).value);
                        (e.target as HTMLInputElement).value = '';
                      }
                    }}
                  />
                </div>
              );
            }}
          />

          {/* Documentation URL */}
          <Controller
            name="documentation_url"
            control={control}
            render={({ field }) => (
              <div className="space-y-1.5">
                <Label htmlFor="documentation_url">{t('admin.systemTemplates.form.documentationUrl')}</Label>
                <Input
                  id="documentation_url"
                  {...field}
                  placeholder={t('admin.systemTemplates.form.documentationUrlPlaceholder')}
                />
                {errors.documentation_url && <p className="text-xs text-destructive">{errors.documentation_url.message}</p>}
              </div>
            )}
          />

        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/integrations/templates')}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting || updateMutation.isPending || !isDirty}>
            <Save className="w-4 h-4 mr-2" />
            {t('common.save')}
          </Button>
        </div>
      </form>
      )}
    </div>
  );
}
